import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { DviQualityScoreCard } from '@/components/dvi/DviQualityScoreCard';
import { DviApprovalTrendsChart } from '@/components/dvi/DviApprovalTrendsChart';
import { DviDriftAlertsList } from '@/components/dvi/DviDriftAlertsList';
import { DviBenchmarkComparison } from '@/components/dvi/DviBenchmarkComparison';
import { DviNetworkOptIn } from '@/components/dvi/DviNetworkOptIn';
import { TopBar } from '@/components/layout/TopBar';
import { useDviSignals } from '@/hooks/useDviSignals';
import { useDviBenchmarks } from '@/hooks/useDviBenchmarks';
import { useDviDrift } from '@/hooks/useDviDrift';
import { Brain, TrendingUp, AlertTriangle, BarChart3, Settings, ArrowLeft } from 'lucide-react';

export default function DviIntelligenceDashboard() {
  const { activeShop } = useAuth();
  const flags = useFeatureFlags();
  const navigate = useNavigate();

  const shopId = activeShop?.id ?? null;
  const { data: signals, isLoading: signalsLoading } = useDviSignals(shopId, 90);
  const { data: benchmarks, isLoading: benchmarksLoading } = useDviBenchmarks(shopId);
  const { data: driftData, isLoading: driftLoading } = useDviDrift(shopId);

  // Feature gate - Enterprise only with OptiDVI addon
  if (!flags.dviNetworkIntelligence) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Brain className="h-6 w-6 text-primary" />
              DVI Intelligence Dashboard
            </h1>
            <p className="text-muted-foreground">
              Network insights, benchmarks, and pattern detection
            </p>
          </div>
          <Button onClick={() => navigate('/shop-profile?tab=admin')} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="benchmarks" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Benchmarks
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Alerts
            {driftData?.has_alerts && (
              <span className="ml-1 h-2 w-2 rounded-full bg-destructive" />
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <DviQualityScoreCard 
              signals={signals} 
              isLoading={signalsLoading} 
            />
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Sample Size</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {signals?.sample_count ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  findings in last 90 days
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Network Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {activeShop?.dvi_network_opt_in ? 'Active' : 'Inactive'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {activeShop?.dvi_network_opt_in 
                    ? 'Contributing to industry intelligence' 
                    : 'Opt-in to unlock benchmarks'}
                </p>
              </CardContent>
            </Card>
          </div>

          <DviApprovalTrendsChart signals={signals} isLoading={signalsLoading} />
        </TabsContent>

        <TabsContent value="benchmarks" className="space-y-4">
          <DviBenchmarkComparison 
            benchmarks={benchmarks} 
            isLoading={benchmarksLoading}
            shopOptedIn={activeShop?.dvi_network_opt_in ?? false}
          />
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <DviDriftAlertsList 
            driftData={driftData} 
            isLoading={driftLoading} 
          />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <DviNetworkOptIn shopId={shopId} />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
